import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { uploadFile } from "../../../helpers/API";
import { guarantorFieldConfig, GUARANTOR_INITIAL_STATE } from "../formConfig";
import { isValidIndianMobile } from "../../../helpers/mobileValidation";

/**
 * Custom hook that encapsulates all guarantor-related state and handlers.
 */
const useGuarantors = () => {
    const { t } = useTranslation();

    const [guarantors, setGuarantors] = useState([]);
    const [showGuarantorModal, setShowGuarantorModal] = useState(false);
    const [editingGuarantorId, setEditingGuarantorId] = useState(null);
    const [guarantorForm, setGuarantorForm] = useState({ ...GUARANTOR_INITIAL_STATE });

    // Photo/signature upload states
    const [guarantorPhotoUploading, setGuarantorPhotoUploading] = useState(false);
    const [guarantorPhotoPreview, setGuarantorPhotoPreview] = useState(null);
    const [guarantorSignatureUploading, setGuarantorSignatureUploading] = useState(false);
    const [guarantorSignaturePreview, setGuarantorSignaturePreview] = useState(null);

    const handleGuarantorFormChange = (field, value) => {
        setGuarantorForm((prev) => ({ ...prev, [field]: value }));
    };

    const resetGuarantorForm = () => {
        setGuarantorForm({ ...GUARANTOR_INITIAL_STATE });
        setGuarantorPhotoPreview(null);
        setGuarantorSignaturePreview(null);
    };

    const handleAddGuarantor = () => {
        const missingRequired = guarantorFieldConfig.some(
            (field) => field.required && !guarantorForm[field.name]
        );
        if (missingRequired) {
            toast.error(t("customer.add.validation.pleaseFillGuarantorFields"));
            return;
        }
        if (!isValidIndianMobile(guarantorForm.mobileNumber)) {
            toast.error(t("customer.add.validation.mobileNumberInvalid"));
            return;
        }
        if (
            guarantorForm.alternateMobile &&
            !isValidIndianMobile(guarantorForm.alternateMobile)
        ) {
            toast.error(t("customer.add.validation.alternateMobileInvalid"));
            return;
        }
        if (editingGuarantorId) {
            setGuarantors((prev) =>
                prev.map((g) => (g.id === editingGuarantorId ? { ...g, ...guarantorForm } : g))
            );
            setEditingGuarantorId(null);
        } else {
            const guarantor = { id: Date.now(), ...guarantorForm };
            setGuarantors((prev) => [...prev, guarantor]);
        }
        resetGuarantorForm();
        setShowGuarantorModal(false);
        toast.success(t("customer.add.messages.saveGuarantorSuccess"));
    };

    const handleEditGuarantor = (guarantor) => {
        setGuarantorForm({
            fullName: guarantor.fullName || "",
            fatherName: guarantor.fatherName || "",
            mobileNumber: guarantor.mobileNumber || "",
            alternateMobile: guarantor.alternateMobile || "",
            address: guarantor.address || "",
            idProofType: guarantor.idProofType || "",
            idNumber: guarantor.idNumber || "",
            photo: guarantor.photo || null,
            signature: guarantor.signature || null,
            occupation: guarantor.occupation || "",
            monthlyIncome: guarantor.monthlyIncome || "",
            employerBusinessName: guarantor.employerBusinessName || "",
            relationshipWithBorrower: guarantor.relationshipWithBorrower || "",
            term: guarantor.term || "",
            termsCondition: guarantor.termsCondition || "",
        });
        setGuarantorPhotoPreview(guarantor.photo || null);
        setGuarantorSignaturePreview(guarantor.signature || null);
        setEditingGuarantorId(guarantor.id);
        setShowGuarantorModal(true);
    };

    const handleRemoveGuarantor = (id) => {
        setGuarantors((prev) => prev.filter((g) => g.id !== id));
    };

    const handleGuarantorPhotoUpload = async (file) => {
        if (!file) return;
        setGuarantorPhotoUploading(true);
        setGuarantorPhotoPreview(URL.createObjectURL(file));
        try {
            const formData = new FormData();
            formData.append("file", file);
            const res = await uploadFile(formData);
            handleGuarantorFormChange("photo", res?.data?.url || res?.url || res?.data || "");
            toast.success(t("customer.add.messages.photoUploadSuccess"));
        } catch (error) {
            console.error("Guarantor photo upload error:", error);
            toast.error(t("customer.add.messages.photoUploadError"));
            setGuarantorPhotoPreview(null);
            handleGuarantorFormChange("photo", null);
        } finally {
            setGuarantorPhotoUploading(false);
        }
    };

    const handleGuarantorSignatureUpload = async (file) => {
        if (!file) return;
        setGuarantorSignatureUploading(true);
        setGuarantorSignaturePreview(URL.createObjectURL(file));
        try {
            const formData = new FormData();
            formData.append("file", file);
            const res = await uploadFile(formData);
            handleGuarantorFormChange("signature", res?.data?.url || res?.url || res?.data || "");
            toast.success(t("customer.add.messages.photoUploadSuccess"));
        } catch (error) {
            console.error("Guarantor signature upload error:", error);
            toast.error(t("customer.add.messages.photoUploadError"));
            setGuarantorSignaturePreview(null);
            handleGuarantorFormChange("signature", null);
        } finally {
            setGuarantorSignatureUploading(false);
        }
    };

    const closeGuarantorModal = () => {
        setShowGuarantorModal(false);
        setEditingGuarantorId(null);
        resetGuarantorForm();
    };

    const openAddGuarantorModal = () => {
        resetGuarantorForm();
        setEditingGuarantorId(null);
        setShowGuarantorModal(true);
    };

    return {
        guarantors,
        setGuarantors,
        showGuarantorModal,
        editingGuarantorId,
        guarantorForm,
        guarantorPhotoUploading,
        guarantorPhotoPreview,
        guarantorSignatureUploading,
        guarantorSignaturePreview,
        handleGuarantorFormChange,
        resetGuarantorForm,
        handleAddGuarantor,
        handleEditGuarantor,
        handleRemoveGuarantor,
        handleGuarantorPhotoUpload,
        handleGuarantorSignatureUpload,
        closeGuarantorModal,
        openAddGuarantorModal,
    };
};

export default useGuarantors;
